'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface GrowthTactic {
    id: string;
    title: string;
    description: string;
    timeRequired: string;
    expectedSignups: string;
    steps: string[];
    status: 'available' | 'coming_soon';
}

const growthTactics: GrowthTactic[] = [
    {
        id: 'personal-network',
        title: 'Share with Your Network',
        description: 'The easiest first step. Post on your personal LinkedIn, Twitter, or send direct messages to friends and colleagues who might be interested.',
        timeRequired: '15 mins',
        expectedSignups: '5-20',
        status: 'available',
        steps: [
            'Write a short post explaining what you\'re building and why',
            'Share your waitlist link on LinkedIn and Twitter',
            'Send direct messages to 10-15 people who might genuinely care',
            'Ask close contacts to share with their networks',
        ],
    },
    {
        id: 'communities',
        title: 'Post in Relevant Communities',
        description: 'Find 3-5 online communities where your target audience hangs out. Reddit, Facebook groups, Discord servers, Slack communities.',
        timeRequired: '30 mins',
        expectedSignups: '10-50',
        status: 'available',
        steps: [
            'Identify communities where your target users are active',
            'Read the rules - most don\'t allow direct promotion',
            'Share genuinely: explain the problem you\'re solving, ask for feedback',
            'Engage with comments and questions authentically',
        ],
    },
    {
        id: 'cold-dms',
        title: 'Direct Outreach',
        description: 'Reach out to 20-30 people who fit your target audience. Keep it personal and short. Ask for feedback, not just signups.',
        timeRequired: '1 hour',
        expectedSignups: '5-15',
        status: 'available',
        steps: [
            'Find people on Twitter/LinkedIn who match your target audience',
            'Send a short, personalized message (not a pitch)',
            'Ask if the problem you\'re solving resonates with them',
            'If yes, share your waitlist link',
        ],
    },
    {
        id: 'twitter-thread',
        title: 'Build in Public Post',
        description: 'Write a Twitter/X thread or LinkedIn post about what you\'re building, why, and what problem it solves. People love following journeys.',
        timeRequired: '30 mins',
        expectedSignups: '10-30',
        status: 'available',
        steps: [
            'Start with the problem you experienced or observed',
            'Explain your solution in simple terms',
            'Share what stage you\'re at (building, validating)',
            'End with your waitlist link and ask for feedback',
        ],
    },
    {
        id: 'email-contacts',
        title: 'Email Your Contacts',
        description: 'If you have any existing email contacts, newsletter subscribers, or past colleagues - send a quick personal email.',
        timeRequired: '20 mins',
        expectedSignups: '5-25',
        status: 'available',
        steps: [
            'Make a list of people who might find this valuable',
            'Write a short, personal email (not a marketing blast)',
            'Explain what you\'re building and why you thought of them',
            'Ask if they\'d be interested in early access',
        ],
    },
    {
        id: 'comment-engagement',
        title: 'Engage in Discussions',
        description: 'Find discussions about the problem you\'re solving. Add value first, mention your solution only when relevant.',
        timeRequired: '30 mins/day',
        expectedSignups: '5-15',
        status: 'available',
        steps: [
            'Search Twitter, Reddit, Quora for discussions about your problem space',
            'Add genuinely helpful comments and insights',
            'Only mention your waitlist if someone asks or it\'s truly relevant',
            'Build relationships, not just signups',
        ],
    },
    {
        id: 'referral-program',
        title: 'Ask for Referrals',
        description: 'After someone signs up, ask them to share with 2-3 friends who might also be interested. Simple but effective.',
        timeRequired: '10 mins setup',
        expectedSignups: '2x multiplier',
        status: 'coming_soon',
        steps: [
            'Add a thank-you message after signup',
            'Ask them to share with friends who have the same problem',
            'Make sharing easy with pre-written copy',
            'Consider offering early access perks for referrals',
        ],
    },
];

const GrowthInteractive = () => {
    const [expandedTactic, setExpandedTactic] = useState<string | null>(null);

    const availableTactics = growthTactics.filter(t => t.status === 'available');
    const totalTime = '3-4 hours';
    const totalSignups = '50-150';

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Waitlist Promotion Tactics</h1>
                <p className="text-lg text-gray-600 font-medium">
                    Quick, practical ways to get your first 50-200 waitlist signups. No budget required.
                </p>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
                <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                        <Icon name="ClipboardDocumentListIcon" size={24} className="text-violet-600" />
                    </div>
                    <div>
                        <div className="text-2xl sm:text-3xl font-bold text-gray-900">{availableTactics.length}</div>
                        <div className="text-sm text-gray-500 font-medium">Tactics</div>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                        <Icon name="ClockIcon" size={24} className="text-blue-600" />
                    </div>
                    <div>
                        <div className="text-2xl sm:text-3xl font-bold text-gray-900">{totalTime}</div>
                        <div className="text-sm text-gray-500 font-medium">Total Time</div>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                        <Icon name="UserGroupIcon" size={24} className="text-emerald-600" />
                    </div>
                    <div>
                        <div className="text-2xl sm:text-3xl font-bold text-emerald-600">{totalSignups}</div>
                        <div className="text-sm text-gray-500 font-medium">Expected Signups</div>
                    </div>
                </div>
            </div>

            {/* Tactics List */}
            <div className="space-y-3">
                {growthTactics.map((tactic, index) => {
                    const isExpanded = expandedTactic === tactic.id;
                    const isDisabled = tactic.status !== 'available';

                    return (
                        <div
                            key={tactic.id}
                            className={`bg-white border rounded-lg overflow-hidden transition-all ${isExpanded ? 'border-gray-300 shadow-sm' : 'border-gray-200'
                                } ${isDisabled ? 'opacity-60' : ''}`}
                        >
                            <button
                                onClick={() => !isDisabled && setExpandedTactic(isExpanded ? null : tactic.id)}
                                disabled={isDisabled}
                                className={`w-full p-4 text-left ${isDisabled ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'
                                    }`}
                            >
                                {/* Mobile-first layout */}
                                <div className="flex items-start gap-3">
                                    {/* Number */}
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${isExpanded
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-gray-100 text-gray-600'
                                        }`}>
                                        {index + 1}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        {/* Title row with chevron on right */}
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className={`text-base sm:text-lg font-semibold ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
                                                    {tactic.title}
                                                </h3>
                                                {tactic.status === 'coming_soon' && (
                                                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">
                                                        Soon
                                                    </span>
                                                )}
                                            </div>
                                            {!isDisabled && (
                                                <Icon
                                                    name="ChevronDownIcon"
                                                    size={20}
                                                    className={`text-gray-400 transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                                                />
                                            )}
                                        </div>

                                        {/* Time & Signups - horizontal on mobile */}
                                        <div className="flex items-center gap-4 mb-3">
                                            <div className="flex items-center gap-1.5">
                                                <Icon name="ClockIcon" size={14} className="text-gray-400" />
                                                <span className="text-sm font-medium text-gray-600">{tactic.timeRequired}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <Icon name="UserGroupIcon" size={14} className="text-emerald-500" />
                                                <span className="text-sm font-semibold text-emerald-600">{tactic.expectedSignups} signups</span>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <p className={`text-sm sm:text-base leading-relaxed ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                                            {tactic.description}
                                        </p>
                                    </div>
                                </div>
                            </button>

                            {/* Expanded Steps */}
                            {isExpanded && (
                                <div className="px-4 pb-4 border-t border-gray-100">
                                    <div className="pt-4 pl-11 sm:pl-11">
                                        <h4 className="text-base font-semibold text-gray-900 mb-3">How to do it:</h4>
                                        <ol className="space-y-3 text-sm sm:text-base text-gray-700">
                                            {tactic.steps.map((step, stepIndex) => (
                                                <li key={stepIndex} className="flex gap-3">
                                                    <span className="text-gray-500 font-semibold shrink-0">{stepIndex + 1}.</span>
                                                    <span>{step}</span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Bottom Note */}
            <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-base text-gray-700">
                    <span className="font-semibold">Remember:</span> The goal is validation, not viral growth.
                    50-100 genuine signups from people who have the problem you're solving is more valuable
                    than 1,000 random signups.
                </p>
            </div>
        </div>
    );
};

export default GrowthInteractive;
